import { ASM_CONTRACT_MAJOR, ASM_CONTRACT_MINOR, type AsmContractName } from "./schemas.js";

export const ASM_CAPABILITIES = [
	"memory.read",
	"memory.write",
	"evidence.read",
	"retrieval.query",
	"intent.parse",
	"intent.route",
	"policy.decide",
	"policy.approval",
	"execution.idempotent",
	"feedback.write",
	"state.snapshot",
	"inference.manifest",
] as const;

export type AsmCapability = (typeof ASM_CAPABILITIES)[number];

export interface ContractVersionRange {
	major: number;
	minMinor: number;
	maxMinor: number;
}

export interface AsmPeerHello {
	protocol: "asm-contracts";
	protocolVersion: 1;
	contracts: Partial<Record<AsmContractName, readonly ContractVersionRange[]>>;
	capabilities: readonly AsmCapability[];
}

export interface NegotiatedContractVersion {
	major: number;
	minor: number;
}

export interface AsmNegotiationResult {
	contracts: Partial<Record<AsmContractName, NegotiatedContractVersion>>;
	capabilities: AsmCapability[];
}

export const DEFAULT_ASM_HELLO: AsmPeerHello = {
	protocol: "asm-contracts",
	protocolVersion: 1,
	contracts: Object.fromEntries(
		(
			[
				"memory",
				"evidence",
				"retrieval",
				"intent",
				"policy",
				"feedback",
				"stateSnapshot",
				"inferenceManifest",
			] as const
		).map((name) => [name, [{ major: ASM_CONTRACT_MAJOR, minMinor: 0, maxMinor: ASM_CONTRACT_MINOR }]]),
	),
	capabilities: ASM_CAPABILITIES,
};

export function negotiateAsmContracts(local: AsmPeerHello, remote: AsmPeerHello): AsmNegotiationResult {
	if (local.protocol !== remote.protocol || local.protocolVersion !== remote.protocolVersion) {
		throw new Error("Incompatible ASM contract protocol");
	}
	const contracts: Partial<Record<AsmContractName, NegotiatedContractVersion>> = {};
	for (const name of Object.keys(local.contracts) as AsmContractName[]) {
		const localRanges = local.contracts[name] ?? [];
		const remoteRanges = remote.contracts[name] ?? [];
		const candidates: NegotiatedContractVersion[] = [];
		for (const left of localRanges) {
			for (const right of remoteRanges) {
				if (left.major !== right.major) continue;
				const minMinor = Math.max(left.minMinor, right.minMinor);
				const maxMinor = Math.min(left.maxMinor, right.maxMinor);
				if (minMinor <= maxMinor) candidates.push({ major: left.major, minor: maxMinor });
			}
		}
		candidates.sort((left, right) => right.major - left.major || right.minor - left.minor);
		if (candidates[0]) contracts[name] = candidates[0];
	}
	const remoteCapabilities = new Set(remote.capabilities);
	return {
		contracts,
		capabilities: local.capabilities.filter((capability) => remoteCapabilities.has(capability)),
	};
}

export function requireNegotiatedCapability(
	result: AsmNegotiationResult,
	contract: AsmContractName,
	capability: AsmCapability,
): NegotiatedContractVersion {
	const version = result.contracts[contract];
	if (!version) throw new Error(`ASM contract not negotiated: ${contract}`);
	if (!result.capabilities.includes(capability)) throw new Error(`ASM capability not negotiated: ${capability}`);
	return version;
}
